// Analytics Utils Service - Shared Utilities & Helper Functions
// Extracted from metaTracking.js for better separation of concerns

import { debugLog, debugError, debugWarn } from '../debugUtils';
import {
  isPreviewOrInternalDomain,
  shouldInitializeAnalytics,
} from '../environmentUtils';

// Meta Pixel ID - only use if configured, no fallback to production
const META_PIXEL_ID = process.env.GATSBY_META_PIXEL_ID;

// Import Analytics at top level to avoid dynamic import issues in tests
let trackEventFunction = null;
try {
  // Only import in browser environment to avoid SSR issues
  if (typeof window !== 'undefined') {
    import('../../components/Analytics')
      .then(({ trackEvent }) => {
        trackEventFunction = trackEvent;
      })
      .catch(err => {
        debugWarn('Failed to load Analytics for tracking:', err);
      });
  }
} catch (err) {
  // Silently handle import errors
}

/**
 * Validate configuration on module load
 */
export const validateConfiguration = () => {
  const issues = [];

  // Check environment first
  if (!shouldInitializeAnalytics()) {
    debugLog('🚫 Meta tracking disabled - not on main production domain');
    return false;
  }

  if (!META_PIXEL_ID) {
    debugLog('⚠️ Meta Pixel ID not configured, Meta tracking will be disabled');
    return false; // Don't initialize Meta tracking without pixel ID
  }

  if (META_PIXEL_ID === 'YOUR_PIXEL_ID_HERE') {
    issues.push(
      'Meta Pixel ID is not configured. Set GATSBY_META_PIXEL_ID environment variable.'
    );
  }

  // Only check for fbq in production for analytics tracking, don't spam console
  if (typeof window !== 'undefined') {
    if (!window.fbq) {
      // Meta Pixel not available
    } else {
      debugLog('✅ Meta Pixel (fbq) detected and ready');
    }
  }

  // In production, be more lenient - only block for critical issues
  if (process.env.NODE_ENV === 'production') {
    const criticalIssues = issues.filter(issue =>
      issue.includes('Meta Pixel ID is not configured')
    );
    if (criticalIssues.length > 0) {
      debugError('🚨 Critical Meta Tracking Issues:', criticalIssues);
    }
    return criticalIssues.length === 0;
  }

  return issues.length === 0;
};

/**
 * Helper function to hash email with SHA-256 (same as server-side)
 */
export const hashEmail = async email => {
  if (!email) return null;

  // Normalize email (lowercase, trim)
  const normalizedEmail = email.toLowerCase().trim();

  // Use Web Crypto API if available (modern browsers)
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(normalizedEmail);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      debugWarn('Web Crypto API failed, using fallback hash:', error);
    }
  }

  // Fallback: simple hash (not cryptographically secure, but consistent)
  let hash = 0;
  for (let i = 0; i < normalizedEmail.length; i++) {
    const char = normalizedEmail.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

/**
 * Helper function to clean payload of undefined/null/empty values before sending to API
 */
export const cleanPayload = payload => {
  const cleaned = {};

  for (const [key, value] of Object.entries(payload)) {
    // Only include meaningful values
    if (value !== null && value !== undefined && value !== '') {
      // For arrays, only include if they have elements
      if (Array.isArray(value)) {
        if (value.length > 0) {
          cleaned[key] = value;
        }
      } else if (typeof value === 'object') {
        // For objects, recursively clean them
        const cleanedObject = cleanPayload(value);
        if (Object.keys(cleanedObject).length > 0) {
          cleaned[key] = cleanedObject;
        }
      } else {
        cleaned[key] = value;
      }
    }
  }

  return cleaned;
};

/**
 * Retry logic for API calls
 */
export const fetchWithRetry = async (url, options, maxRetries = 1) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      return response; // Return successful response
    } catch (error) {
      lastError = error;

      // Only retry on network errors, not on HTTP errors
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 2000); // Exponential backoff, max 2s
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  throw lastError;
};

/**
 * Helper function to generate consistent event IDs for deduplication
 */
export const generateEventId = (
  eventType,
  identifier,
  timestamp = Date.now()
) => {
  // Require meaningful identifier to prevent event ID collisions
  if (!identifier || identifier.trim() === '') {
    throw new Error(
      `Cannot generate reliable event ID for ${eventType}: missing required identifier. Event IDs need unique identifiers to prevent collisions.`
    );
  }

  // Apply different deduplication strategies based on event type
  let finalTimestamp;

  if (eventType === 'Lead') {
    // For Lead events: Round to 7-day intervals (full Meta attribution window)
    // Captures complete awareness → consideration journey as single lead
    const date = new Date(timestamp);
    const daysSinceEpoch = Math.floor(date.getTime() / (1000 * 60 * 60 * 24));
    const roundedDaysSinceEpoch = Math.floor(daysSinceEpoch / 7) * 7; // Round to 7-day intervals
    const roundedDate = new Date(roundedDaysSinceEpoch * 24 * 60 * 60 * 1000);
    finalTimestamp = roundedDate.getTime();

    debugLog(
      `🔄 Using 7-day deduplication for Lead event: ${identifier.substring(0, 3)}*** (week starting ${roundedDate.toDateString()})`
    );
  } else if (eventType === 'InitiateCheckout') {
    // For InitiateCheckout events: Round to 15-minute intervals
    // Captures technical retries/errors while treating longer gaps as separate purchase intents
    const date = new Date(timestamp);
    const minutes = date.getMinutes();
    const roundedMinutes = Math.floor(minutes / 15) * 15; // Round down to nearest 15-minute interval
    const rounded15Min = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      roundedMinutes
    );
    finalTimestamp = rounded15Min.getTime();

    debugLog(
      `🔄 Using 15-minute deduplication for InitiateCheckout event: ${identifier.substring(0, 3)}*** at ${rounded15Min.toLocaleString()}`
    );
  } else {
    // For PageView and Purchase events: Use full timestamp (track every occurrence)
    finalTimestamp = timestamp;
  }

  // Generate reliable event ID with meaningful identifier
  const baseString = `${eventType}_${identifier.trim()}_${finalTimestamp}`;

  // Simple hash function to create consistent ID
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString();
};

/**
 * Generate deterministic event ID for Purchase events using payment intent ID
 * This ensures both client-side and webhook use the same event ID
 */
export const generatePurchaseEventId = (paymentIntentId, email) => {
  if (!paymentIntentId) {
    throw new Error(
      'Payment intent ID is required for deterministic Purchase event ID'
    );
  }

  // Use payment intent ID as the deterministic base (same for client and webhook)
  const baseString = `Purchase_${paymentIntentId}_${email}`;

  // Simple hash function to create consistent ID
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString();
};

/**
 * Check if tracking should be skipped for preview/internal domains
 */
export const shouldSkipTracking = () => {
  if (isPreviewOrInternalDomain()) {
    debugLog(
      `🚫 Meta tracking skipped - preview/internal domain:`,
      typeof window !== 'undefined' ? window.location.hostname : 'server-side'
    );
    return {
      skip: true,
      reason: 'Tracking disabled on preview/internal domains',
    };
  }
  return { skip: false };
};

/**
 * Get Meta Pixel ID for tracking
 */
export const getMetaPixelId = () => META_PIXEL_ID;

/**
 * Check if we're in a browser environment
 */
export const isBrowserEnvironment = () => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

/**
 * Get tracking event function for analytics
 */
export const getTrackEventFunction = () => trackEventFunction;

/**
 * Safely access window.fbq
 */
export const getMetaPixel = () => {
  return isBrowserEnvironment() && typeof window.fbq === 'function'
    ? window.fbq
    : null;
};

/**
 * Store user data for enhanced tracking
 */
export const storeUserDataForTracking = async data => {
  if (data.email) {
    try {
      const { storeUserData } = await import('../userDataPersistence');
      await storeUserData({
        email: data.email,
      });
      debugLog(`💾 User data stored for enhanced PageView matching`);
    } catch (error) {
      debugLog(`⚠️ Failed to store user data:`, error);
    }
  }
};
